import { useAppSelector } from "@app/hooks"
import {
  CalendarHubClient,
  ProgramManagementCalendar,
} from "@clients/calendarHubClient"
import { LoadingComponent } from "@cmp/loaders/loadingComponent"
import { selectLatestPublishedCoursesByIds } from "@features/coursesMenu/coursesSelectors"
import { selectLoggedInUser } from "@features/login/loginSelectors"
import { Role } from "@features/login/loginSlice"
import { selectProgramsWithTracks } from "@features/program/programSliceSelectors"
import { selectTerms } from "@features/term/termSelector"
import { ProgramDomain } from "@masterschool/course-builder-api"
import MsCalendar from "@masterschool/ui-calendar"
import { BuildEntityLink } from "@masterschool/ui-calendar/dist/src/types"
import { Stack } from "@mui/material"
import { DateRange, defaultDateRange } from "@utils/date"
import { domainToDisplayName } from "@utils/domainUtils"
import { useCallback, useMemo, useState } from "react"
import {
  buildInstructorGroupings,
  buildMentorAndCsaGroupings,
  buildMsCalendarGroupingsAndFiltersForProgramOwner,
  mapToMsCalendarProps,
} from "./calendarMappers"
import { FetchEventSnackbar } from "./FetchEventSnackbar"
import { useEventActions } from "./useEventActions"
import {
  useGetCalendarById,
  useGetCalendarsForProgramOwner,
  useGetInstructorCalendars,
  useGetMentorCalendars,
  useGetCsaCalendars,
  useLazyPastPrograms,
  useLazyProgramClassCalendars,
} from "./useGetCalendar"

const CalendarContainer = () => {
  // Lives in the parent so it survives the menu's refetch. null means "first
  // load" — the backend scopes to the staff member's home school(s). Once the
  // user toggles a school the selection becomes explicit (seeded from home).
  const [selectedDomains, setSelectedDomains] = useState<
    ProgramDomain[] | null
  >(null)
  const {
    calendars: calendarsForMenu,
    homeDomains,
    isFetching: isLoadingCalendars,
  } = useGetCalendarsForProgramOwner(true, selectedDomains)
  const [dateRange, setDateRange] = useState<DateRange>(defaultDateRange)

  // Only blank the whole view on the very first load. A toggle refetch keeps the
  // current calendar mounted (so its toggle state isn't lost) and surfaces the
  // in-flight state through the menu action's helper text instead.
  const hasLoadedOnce =
    calendarsForMenu.programSchoolCalendars.length > 0 ||
    calendarsForMenu.programStaffCalendars.length > 0 ||
    calendarsForMenu.studentCalendars.length > 0
  if (isLoadingCalendars && !hasLoadedOnce) {
    return (
      <Stack width={1} height={1} alignItems="center" justifyContent="center">
        <LoadingComponent loadedComponents="" />
      </Stack>
    )
  }

  return (
    <CalendarWithEvent
      calendarsForMenu={calendarsForMenu}
      dateRange={dateRange}
      onDatesChange={setDateRange}
      selectedDomains={selectedDomains}
      homeDomains={homeDomains}
      onSelectedDomainsChange={setSelectedDomains}
      isRefetchingCalendars={isLoadingCalendars}
    />
  )
}

const CalendarWithEvent = ({
  calendarsForMenu,
  dateRange,
  onDatesChange,
  selectedDomains,
  homeDomains,
  onSelectedDomainsChange,
  isRefetchingCalendars,
}: {
  calendarsForMenu: ProgramManagementCalendar
  dateRange: DateRange
  onDatesChange: (dateRange: DateRange) => void
  selectedDomains: ProgramDomain[] | null
  homeDomains: ProgramDomain[]
  onSelectedDomainsChange: (domains: ProgramDomain[] | null) => void
  isRefetchingCalendars: boolean
}) => {
  const programs = useAppSelector(selectProgramsWithTracks)
  const user = useAppSelector(selectLoggedInUser)
  const userId = user?.clientId

  // Instructors get their own calendars (personal + staffed classes) added to
  // the menu; the program-owner menu alone shows them nothing of their own.
  const isInstructor = user?.roles.includes(Role.Instructor) ?? false
  const instructorCalendars = useGetInstructorCalendars(isInstructor)
  const isMentor = user?.roles.includes(Role.Mentor) ?? false
  const mentorCalendars = useGetMentorCalendars(isMentor)
  const isCsa = user?.roles.includes(Role.CareerSuccessAdvisor) ?? false
  const csaCalendars = useGetCsaCalendars(isCsa)
  // With no saved selection an instructor starts with ALL their calendars on
  // (personal + assigned classes) — a fresh device must never open on a blank
  // or partial schedule. Scoped to exactly these ids so a mixed-role user's
  // program-owner calendars aren't dragged into the default.
  const instructorDefaultCalendarIds = useMemo(() => {
    if (!isInstructor) {
      return undefined
    }
    return [
      ...(instructorCalendars.personalCalendar
        ? [instructorCalendars.personalCalendar.id]
        : []),
      ...instructorCalendars.schoolCalendars.map((calendar) => calendar.id),
    ]
  }, [isInstructor, instructorCalendars])

  // Admins default to running programs; past programs load lazily under a
  // collapsible so the initial menu stays light. MasterschoolEmployee is
  // intentionally excluded — a plain employee is no longer a calendar admin (the
  // backend returns an empty program-calendar set for them), so we don't render
  // the "Past programs" admin section for them.
  const isAdmin =
    user?.roles.some((r) =>
      [Role.SchoolMasterAdmin, Role.LmsAdmin].includes(r),
    ) ?? false
  const lazyPastPrograms = useLazyPastPrograms(isAdmin)

  // Per-school filter toggles. The staff member's home school(s) load by
  // default; every other school they can see (the domains present in the
  // programs they manage — same redux SoT the menu groups by) becomes a toggle
  // that lazy-fetches that school's programs when switched on. No role gating:
  // all staff get the toggles, a single-school staff member just sees one.
  const availableDomains = useMemo(
    () =>
      [
        ...new Set(
          programs
            .map((p) => p.domain)
            .filter((d): d is ProgramDomain => d !== undefined),
        ),
      ].sort((a, b) =>
        domainToDisplayName(a).localeCompare(domainToDisplayName(b)),
      ),
    [programs],
  )
  // Until the user touches a toggle, "selected" reflects the home default the
  // backend resolved; their first toggle seeds an explicit set from home.
  const effectiveSelectedDomains = selectedDomains ?? homeDomains
  const toggleDomain = useCallback(
    (domain: ProgramDomain) => {
      const base = selectedDomains ?? homeDomains
      const next = base.includes(domain)
        ? base.filter((d) => d !== domain)
        : [...base, domain]
      // Deselecting the last school falls back to the home default rather than
      // an empty menu — you always see at least your own school.
      onSelectedDomainsChange(next.length > 0 ? next : null)
    },
    [selectedDomains, homeDomains, onSelectedDomainsChange],
  )

  const flatCalendarsForMenu = useMemo(
    () => [
      ...calendarsForMenu.programSchoolCalendars,
      ...calendarsForMenu.programStaffCalendars,
      ...calendarsForMenu.studentCalendars,
      ...(instructorCalendars.personalCalendar
        ? [instructorCalendars.personalCalendar]
        : []),
      ...instructorCalendars.schoolCalendars,
      ...(mentorCalendars.personalCalendar
        ? [mentorCalendars.personalCalendar]
        : []),
      ...mentorCalendars.studentCalendars,
      ...mentorCalendars.schoolCalendars,
      ...(csaCalendars.personalCalendar ? [csaCalendars.personalCalendar] : []),
      ...csaCalendars.studentCalendars,
    ],
    [calendarsForMenu, instructorCalendars, mentorCalendars, csaCalendars],
  )

  const {
    msEvents,
    onChange,
    onDelete,
    addCalendarIdsToFetch,
    setFetchOrganized,
    loading,
    onCalendarDatesChange,
    onMergedCopySave,
  } = useEventActions([], dateRange, onDatesChange)

  const getCalendarById = useGetCalendarById(msEvents, flatCalendarsForMenu)

  // When a manager toggles a student in the menu, resolve that student's
  // class/program calendars so their sessions show — the student's personal
  // calendar holds none of their classes.
  const resolveStudentCalendars = useCallback(
    async (studentCalendarId: string) => {
      const res = await CalendarHubClient.getStudentAttendableCalendars(
        studentCalendarId,
      )
      return [
        ...res.schoolCalendars.map(mapToMsCalendarProps),
        ...res.courseClassCalendars.map(mapToMsCalendarProps),
      ]
    },
    [],
  )

  // Just-in-time navigation: turn the entities the calendar names into links to
  // their LMS pages, staying in this app.
  //  - program:  programId is a ProgramDto.id, exactly what /program/:programId
  //              renders.
  //  - course:   courseId resolves through /course-manager/course/:courseId,
  //              which redirects to that course's current instance page.
  //  - student:  userClientId is the students-tab row id (getRowId keys on
  //              row.studentId, which the backend fills from the client id), so
  //              /students?student=<id> opens the tab with that student expanded.
  // Class calendars carry a course_class id (and study halls a campus offering
  // client id) — NEITHER is the ProgramClassDto.id that /program/:programId/
  // :classId resolves via selectClass, so linking them would be a dead/lying
  // link; they stay plain. People (calendar) rows have no program-management
  // route reachable from here, so they stay plain too.
  const buildEntityLink = useCallback<BuildEntityLink>((entity) => {
    if (entity.type === "program") {
      return { href: `/program/${entity.programId}`, label: "Open program" }
    }
    if (entity.type === "course") {
      return {
        href: `/course-manager/course/${entity.courseId}`,
        label: "Open course",
      }
    }
    if (entity.type === "student") {
      return {
        href: `/students?student=${entity.userClientId}`,
        label: "Open student",
      }
    }
    return null
  }, [])

  // Eager-load the running programs' class calendars on open, so classes are
  // searchable + invitable in the menu and the invite picker without expanding
  // each program one by one. The hook resolves the class IDs reactively (same
  // selector chain the program calendar uses) and fetches as soon as the data
  // is in Redux, so there's no gating to get wrong. Past programs stay behind
  // their lazy section (loaded via loadProgramClasses when expanded).
  const runningProgramIds = useMemo(
    () => [
      ...new Set(
        calendarsForMenu.programSchoolCalendars.map(
          (c) => c.offeringDetails.programId,
        ),
      ),
    ],
    [calendarsForMenu],
  )
  const { classCalendarsByProgram, loadProgramClasses } =
    useLazyProgramClassCalendars(runningProgramIds)

  // Group each program's classes under "Study halls" + per-course groups (the
  // program calendar's structure), or flat when toggled off. Courses + terms
  // are app-wide redux data; descriptors resolve from the classes loaded so far.
  const [groupByCourse, setGroupByCourse] = useState(true)
  const loadedClassCourseIds = useMemo(
    () => [
      ...new Set(
        Object.values(classCalendarsByProgram).flatMap((state) =>
          state.calendars.map((c) => c.courseClassDetails.courseId),
        ),
      ),
    ],
    [classCalendarsByProgram],
  )
  const courseDescriptors = useAppSelector((state) =>
    selectLatestPublishedCoursesByIds(state, loadedClassCourseIds),
  )
  const terms = useAppSelector(selectTerms)

  const { groupings, customFilters } = useMemo(
    () =>
      buildMsCalendarGroupingsAndFiltersForProgramOwner(
        calendarsForMenu,
        programs,
        { classCalendarsByProgram, loadProgramClasses },
        groupByCourse ? { courseDescriptors, terms } : undefined,
      ),
    [
      calendarsForMenu,
      programs,
      classCalendarsByProgram,
      loadProgramClasses,
      groupByCourse,
      courseDescriptors,
      terms,
    ],
  )

  const allGroupings = useMemo(() => {
    const instructorGroupings = buildInstructorGroupings(instructorCalendars)
    // Campus parity: the same person must see the same calendar sets on both
    // apps, so mentor/CSA scopes merge in exactly like instructor calendars.
    // Calendars already listed (own calendar, shared schools) are skipped.
    const alreadyListed = new Set(
      instructorGroupings.flatMap((grouping) =>
        grouping.type === "single"
          ? grouping.calendars.map((calendar) => calendar.id)
          : [],
      ),
    )
    return [
      ...instructorGroupings,
      ...buildMentorAndCsaGroupings(
        mentorCalendars,
        csaCalendars,
        alreadyListed,
      ),
      ...groupings,
    ]
  }, [instructorCalendars, mentorCalendars, csaCalendars, groupings])

  return (
    <Stack width={1} height={1} px={3} pt={8}>
      {loading && <FetchEventSnackbar />}
      <Stack flex={1} overflow="auto">
        <MsCalendar
          events={msEvents}
          calendars={allGroupings}
          lazySection={lazyPastPrograms}
          customCalendarFilters={customFilters}
          getCalendar={getCalendarById}
          onMergedCopySave={onMergedCopySave}
          onEventChange={onChange}
          supportCreateEventTypes={["live-session", "regular"]}
          userId={userId ?? ""}
          defaultSelectedCalendarIds={instructorDefaultCalendarIds}
          onDeleteEvent={onDelete}
          showDraftEvents
          showOrganizedByMe
          canJoinLiveSessions={false}
          menuActions={[
            ...(availableDomains.length > 1
              ? availableDomains.map((domain) => ({
                  label: domainToDisplayName(domain),
                  isActive: effectiveSelectedDomains.includes(domain),
                  helperText: isRefetchingCalendars
                    ? "Loading…"
                    : effectiveSelectedDomains.includes(domain)
                    ? "Showing this school's programs"
                    : "Switch on to load this school's programs",
                  onClick: () => toggleDomain(domain),
                  // Cluster the per-school toggles behind one "Schools" trigger
                  // (hover popover) so they don't fill the sidebar.
                  group: "Schools",
                }))
              : []),
            {
              label: "Group by course",
              isActive: groupByCourse,
              helperText: groupByCourse
                ? "Classes nested under Study halls & course groups"
                : "Classes listed flat under each program",
              onClick: () => setGroupByCourse((value) => !value),
            },
          ]}
          resolveStudentCalendars={resolveStudentCalendars}
          buildEntityLink={buildEntityLink}
          onSelectedCalendarChange={(calendarIds: string[]) => {
            setFetchOrganized(calendarIds.includes("organized"))
            const realCalendarIds = calendarIds.filter(
              (c) => !c.includes("draft") && !c.includes("organized"),
            )
            addCalendarIdsToFetch(realCalendarIds)
          }}
          onDatesRangeChange={onCalendarDatesChange}
        />
      </Stack>
    </Stack>
  )
}

export default CalendarContainer
